import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { trackConversion } from "@/lib/track";
import {
  PEST_OPTIONS,
  PROPERTY_TYPES,
  URGENCY_OPTIONS,
  CONTACT_METHODS,
} from "@/data/site";
import { DuckBadge } from "@/components/DuckMascot";

const STEPS = ["The problem", "Your place", "Your details"];

export default function LeadForm({ presetPest, onSuccess }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    pest: presetPest || "",
    urgency: "",
    street: "",
    city: "",
    state: "CT",
    zip_code: "",
    property_type: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    preferred_contact: "Email",
    description: "",
    consent: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return form.pest && form.urgency;
    if (step === 1)
      return form.street.trim().length > 3 && /^\d{5}$/.test(form.zip_code) && form.property_type;
    return true;
  };

  const next = () => {
    if (!canNext()) {
      toast.error("Just pick an option to continue.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    if (!form.consent) {
      toast.error("Please agree to be contacted so we can help.");
      return;
    }
    setBusy(true);
    try {
      await submitLead(form);
      // Attribution persists across the session, so an ad click that read a
      // pest guide first and converted here is still a paid conversion.
      trackConversion({
        pest: form.pest,
        city: form.city,
        zip_code: form.zip_code,
        urgency: form.urgency,
      });
      toast.success("Got it — request received!");
      onSuccess?.(form);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const OptionGrid = ({ options, value, onChange, testid, cols = "grid-cols-2 sm:grid-cols-4" }) => (
    <div className={`grid ${cols} gap-2.5`}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`px-4 py-3.5 rounded-2xl border text-sm font-medium transition-all text-left ${
            value === opt
              ? "border-terracotta bg-terracotta/10 text-forest-950 ring-1 ring-terracotta"
              : "border-forest/12 bg-card text-forest-950/75 hover:border-forest/30"
          }`}
          data-testid={`${testid}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );

  const inputCls =
    "w-full px-4 py-3.5 rounded-2xl border border-forest/12 bg-card text-forest-950 placeholder:text-forest-950/40 focus:border-terracotta focus:ring-1 focus:ring-terracotta outline-none transition-colors";
  const labelCls = "block text-sm font-medium text-forest-950 mb-1.5";

  return (
    <div className="rounded-[2rem] border border-forest/10 bg-cream shadow-xl shadow-forest/5 overflow-hidden" data-testid="lead-form">
      {/* Progress */}
      <div className="bg-forest-950 px-6 sm:px-10 py-6">
        <div className="flex items-center gap-3 mb-5">
          <DuckBadge size={44} />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-duckgreen">Find a Pro</p>
            <p className="text-cream font-serif text-lg font-bold">Tell us what's bugging you</p>
          </div>
        </div>
        <div className="flex gap-2">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1">
              <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-duckgreen" : "bg-cream/20"}`} />
              <span className={`mt-1.5 block text-[10px] font-mono uppercase tracking-wider ${i <= step ? "text-cream/80" : "text-cream/40"}`}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={submit} className="px-6 sm:px-10 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {step === 0 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-serif text-xl font-bold text-forest-950 mb-1">What's bugging you?</h3>
                  <p className="text-sm text-forest-950/60 mb-4">Pick the closest match — "Not sure" is a perfectly good answer.</p>
                  <OptionGrid options={PEST_OPTIONS} value={form.pest} onChange={(v) => set("pest", v)} testid="lead-pest" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-bold text-forest-950 mb-3">How soon do you need help?</h3>
                  <OptionGrid options={URGENCY_OPTIONS} value={form.urgency} onChange={(v) => set("urgency", v)} testid="lead-urgency" cols="grid-cols-1 sm:grid-cols-2" />
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-serif text-xl font-bold text-forest-950 mb-1">Where's the problem?</h3>
                  <p className="text-sm text-forest-950/60 mb-4">A provider needs the property address to quote the job.</p>
                  <label className={labelCls} htmlFor="street">Street address</label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-forest-950/40" />
                    <input
                      id="street"
                      name="street"
                      autoComplete="address-line1"
                      value={form.street}
                      onChange={(e) => set("street", e.target.value)}
                      className={`${inputCls} pl-11`}
                      data-testid="lead-form-street-input"
                    />
                  </div>
                  <div className="mt-4 grid grid-cols-[1fr_auto] gap-3">
                    <div>
                      <label className={labelCls} htmlFor="city">Town</label>
                      <input
                        id="city"
                        name="city"
                        autoComplete="address-level2"
                        value={form.city}
                        onChange={(e) => set("city", e.target.value)}
                        className={inputCls}
                        data-testid="lead-form-city-input"
                      />
                    </div>
                    <div className="w-24">
                      <label className={labelCls} htmlFor="state">State</label>
                      <input
                        id="state"
                        name="state"
                        autoComplete="address-level1"
                        value={form.state}
                        onChange={(e) => set("state", e.target.value)}
                        className={inputCls}
                        data-testid="lead-form-state-input"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className={labelCls} htmlFor="zip">ZIP code</label>
                    <input
                      id="zip"
                      name="zip_code"
                      type="tel"
                      inputMode="numeric"
                      maxLength={5}
                      autoComplete="postal-code"
                      value={form.zip_code}
                      onChange={(e) => set("zip_code", e.target.value.replace(/\D/g, ""))}
                      className={inputCls}
                      data-testid="lead-form-zip-input"
                    />
                  </div>
                </div>
                <div>
                  <h3 className="font-serif text-lg font-bold text-forest-950 mb-3">What kind of property?</h3>
                  <OptionGrid options={PROPERTY_TYPES} value={form.property_type} onChange={(v) => set("property_type", v)} testid="lead-property" cols="grid-cols-1 sm:grid-cols-2" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <h3 className="font-serif text-xl font-bold text-forest-950 mb-1">Almost done.</h3>
                  <p className="text-sm text-forest-950/60">We'll review your request and help point you in the right direction.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="fn">First name</label>
                    <input id="fn" name="first_name" autoComplete="given-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={inputCls} data-testid="lead-form-firstname-input" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="ln">Last name</label>
                    <input id="ln" name="last_name" autoComplete="family-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={inputCls} data-testid="lead-form-lastname-input" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="em">Email</label>
                    <input id="em" name="email" type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="lead-form-email-input" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="ph">Phone</label>
                    <input id="ph" name="phone" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="lead-form-phone-input" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Preferred contact method</label>
                  <OptionGrid options={CONTACT_METHODS} value={form.preferred_contact} onChange={(v) => set("preferred_contact", v)} testid="lead-contact" cols="grid-cols-3" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="desc">Briefly, what's going on? <span className="text-forest-950/40 font-normal">(optional)</span></label>
                  <textarea id="desc" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. Mosquitoes are unbearable in the backyard by evening…" className={inputCls} data-testid="lead-form-description-input" />
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="lead-form-consent-label">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => set("consent", e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-forest/30 text-terracotta focus:ring-terracotta"
                    data-testid="lead-form-consent-checkbox"
                  />
                  <span className="text-xs text-forest-950/65 leading-relaxed">
                    I agree that DuckTheBugs may contact me about this request and may share my details with an
                    independent, appropriate pest-control provider. I understand DuckTheBugs is a resource and
                    matching service and does not itself perform pesticide applications. I can opt out at any time.
                  </span>
                </label>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 flex items-center justify-between gap-3">
          {step > 0 ? (
            <button type="button" onClick={back} className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full text-forest-950/70 font-medium hover:bg-pill/70 transition-colors" data-testid="lead-form-back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          ) : (
            <span />
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={next} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta/90 transition-all active:scale-95" data-testid="lead-form-next-btn">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-forest-950 text-cream font-semibold hover:bg-forest transition-all active:scale-95 disabled:opacity-60" data-testid="find-pro-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {busy ? "Sending…" : "Submit request"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
